import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import { ApiError, api, formValues, type Client, type ClientInput } from '../api.js';
import { navigate } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/audit-dialog.js';
import '../components/billing-groups-dialog.js';
import type { BillingGroupsDialog } from '../components/billing-groups-dialog.js';
import type { AuditDialog } from '../components/audit-dialog.js';

@customElement('clients-page')
export class ClientsPage extends LitElement {
  static styles = pageStyles;

  @state() private items: Client[] = [];
  @state() private docCounts: Record<string, number> = {};
  @state() private loading = true;
  @state() private error = '';

  @state() private dialogOpen = false;
  @state() private editing: Client | null = null;
  @state() private saving = false;
  @state() private formError = '';
  @state() private fieldErrors: Record<string, string> = {};
  // What to do with a stored Everhour token on save; only meaningful when editing a client that has one.
  @state() private tokenAction: 'keep' | 'replace' | 'remove' = 'keep';

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = '';
    try {
      [this.items, this.docCounts] = await Promise.all([api.get<Client[]>('/api/clients'), api.documentCounts('client')]);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private openNew() {
    this.editing = null;
    this.resetFormState();
    this.dialogOpen = true;
  }

  private openEdit(item: Client) {
    this.editing = item;
    this.resetFormState();
    this.dialogOpen = true;
  }

  private billing(c: Client) {
    void (this.renderRoot.querySelector('billing-groups-dialog') as BillingGroupsDialog).show(c.id, c.name);
  }

  private history(id: number, label: string) {
    void (this.renderRoot.querySelector('audit-dialog') as AuditDialog).show('client', id, label);
  }

  private resetFormState() {
    this.formError = '';
    this.fieldErrors = {};
    this.saving = false;
    this.tokenAction = 'keep';
  }

  private async save(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    const input: ClientInput = {
      name: v.name ?? '',
      billing_name: v.billing_name,
      reference: v.reference,
      billing_email: v.billing_email,
      country: v.country ?? '',
      tax_id: v.tax_id,
      // A checked wa-switch submits its value; unchecked submits nothing.
      is_domestic: v.is_domestic !== undefined && v.is_domestic !== null,
      default_currency: (v.default_currency ?? 'EUR') as 'EUR' | 'MKD',
      street: v.street,
      postal_code: v.postal_code,
      city: v.city,
      payment_days: Number(v.payment_days ?? 30),
      ...this.tokenInput(v.everhour_api_token),
    };
    this.saving = true;
    this.formError = '';
    this.fieldErrors = {};
    try {
      if (this.editing) {
        await api.put(`/api/clients/${this.editing.id}`, input);
      } else {
        await api.post('/api/clients', input);
      }
      this.dialogOpen = false;
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        this.formError = err.message;
        this.fieldErrors = err.fields;
      } else {
        this.formError = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  /** Write-only token field: omitted keeps the stored token, '' removes it, a value replaces it. */
  private tokenInput(typed: string | null | undefined): { everhour_api_token?: string } {
    if (this.editing?.everhour_token_set) {
      if (this.tokenAction === 'remove') return { everhour_api_token: '' };
      if (this.tokenAction === 'keep') return {};
    }
    return typed ? { everhour_api_token: typed } : {};
  }

  private async deleteItem(item: Client) {
    if (!confirm(`Delete client "${item.name}"?`)) return;
    try {
      await api.delete(`/api/clients/${item.id}`);
      await this.load();
    } catch (err) {
      this.error = (err as Error).message;
    }
  }

  render() {
    return html`
      <div class="page-header">
        <h2>Clients</h2>
        <span class="spacer"></span>
        <wa-button variant="brand" @click=${this.openNew}>New client</wa-button>
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.renderTable()} ${this.renderDialog()}
      <audit-dialog></audit-dialog>
      <billing-groups-dialog></billing-groups-dialog>
    `;
  }

  private renderTable() {
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    if (this.items.length === 0) return html`<div class="empty">No clients yet.</div>`;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Name</th>
            <th>Reference</th>
            <th>Location</th>
            <th>Type</th>
            <th>Tax ID</th>
            <th>Currency</th>
            <th>Payment days</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${this.items.map(
            (c) => html`
              <tr>
                <td data-label="Name">
                  ${c.name}
                  ${c.billing_name && c.billing_name !== c.name ? html`<span class="sub">${c.billing_name}</span>` : nothing}
                </td>
                <td class="m-hide" data-label="Ref.">${c.reference ?? html`<span class="muted">—</span>`}</td>
                <td data-label="Location">${c.city ? `${c.city}, ${c.country}` : c.country}</td>
                <td data-label="VAT">
                  ${c.is_domestic
                    ? html`<wa-tag size="small" variant="warning">domestic · VAT</wa-tag>`
                    : html`<wa-tag size="small" variant="neutral">foreign · 0% VAT</wa-tag>`}
                </td>
                <td class="m-hide" data-label="Tax ID">${c.tax_id ?? html`<span class="muted">—</span>`}</td>
                <td data-label="Currency">${c.default_currency}</td>
                <td class="num m-hide" data-label="Pay days">${c.payment_days}</td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => navigate(`/documents?client_id=${c.id}`)}>Documents (${this.docCounts[c.id] ?? 0})</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.billing(c)}>Billing groups</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.history(c.id, c.name)}>History</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.openEdit(c)}>Edit</wa-button>
                  <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.deleteItem(c)}>Delete</wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }

  private renderTokenField(c: Client | null, err: (f: string) => string) {
    const stored = c?.everhour_token_set ?? false;
    if (stored && this.tokenAction === 'keep') {
      return html`
        <div class="inline-field">
          <span class="label">API token</span>
          <span class="mono">set · ••••${c?.everhour_token_hint ?? ''}</span>
          <wa-button size="small" appearance="plain" @click=${() => (this.tokenAction = 'replace')}>Replace</wa-button>
          <wa-button size="small" appearance="plain" variant="danger" @click=${() => (this.tokenAction = 'remove')}>Remove</wa-button>
        </div>
      `;
    }
    if (stored && this.tokenAction === 'remove') {
      return html`
        <div class="inline-field">
          <span class="label">API token</span>
          <span class="muted">will be removed when you save</span>
          <wa-button size="small" appearance="plain" @click=${() => (this.tokenAction = 'keep')}>Undo</wa-button>
        </div>
      `;
    }
    return html`
      <wa-input
        class="full"
        name="everhour_api_token"
        label="API token"
        type="password"
        password-toggle
        autocomplete="off"
        hint=${err('everhour_api_token') || (stored ? 'Enter the new token; leave empty to keep the current one' : 'Optional; used by the time import. Stored encrypted and never shown again.')}
      ></wa-input>
    `;
  }

  // wa-select and other children emit the same bubbling wa-after-hide when
  // their dropdown closes; only the dialog's own event should close it.
  private onDialogHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.dialogOpen = false;
  };

  private renderDialog() {
    const c = this.editing;
    const err = (f: string) => this.fieldErrors[f] ?? '';
    return html`
      <wa-dialog
        label=${c ? 'Edit client' : 'New client'}
        ?open=${this.dialogOpen}
        @wa-after-hide=${this.onDialogHidden}
        style="--width: min(45rem, calc(100vw - 1.5rem));"
      >
        <form id="client-form" @submit=${this.save}>
          <wa-input name="name" label="Name" required .value=${c?.name ?? ''} hint=${err('name')}></wa-input>
          <wa-input name="billing_name" label="Bill-to name" placeholder="legal name, if different" .value=${c?.billing_name ?? ''} hint=${err('billing_name') || 'Printed on invoices instead of the name'}></wa-input>
          <wa-input name="tax_id" label="VAT / tax ID" .value=${c?.tax_id ?? ''}></wa-input>
          <wa-input name="reference" label="Reference number" placeholder="200300 Software" .value=${c?.reference ?? ''} hint=${err('reference') || "Client's PO / cost centre, printed on invoices"}></wa-input>
          <wa-input class="full" name="billing_email" label="Billing email" type="email" .value=${c?.billing_email ?? ''} hint=${err('billing_email')}></wa-input>

          <h4>Address</h4>
          <wa-input class="full" name="street" label="Street" .value=${c?.street ?? ''}></wa-input>
          <wa-input name="postal_code" label="Postal code" .value=${c?.postal_code ?? ''}></wa-input>
          <wa-input name="city" label="City" .value=${c?.city ?? ''}></wa-input>
          <wa-input name="country" label="Country (ISO code)" required maxlength="2" placeholder="DE" .value=${c?.country ?? ''} hint=${err('country')}></wa-input>

          <h4>Invoicing</h4>
          <wa-select name="default_currency" label="Default currency" .value=${c?.default_currency ?? 'EUR'} hint=${err('default_currency')}>
            <wa-option value="EUR">EUR</wa-option>
            <wa-option value="MKD">MKD</wa-option>
          </wa-select>
          <wa-input name="payment_days" label="Payment terms (days)" type="number" min="0" .value=${String(c?.payment_days ?? 30)} hint=${err('payment_days')}></wa-input>
          <wa-switch class="full" name="is_domestic" ?checked=${c?.is_domestic ?? false} hint="Domestic clients are invoiced with standard VAT; foreign clients with 0%.">Domestic client</wa-switch>

          <h4>Everhour</h4>
          ${this.renderTokenField(c, err)}
          ${this.formError ? html`<wa-callout class="form-error" variant="danger">${this.formError}</wa-callout>` : nothing}
        </form>
        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.dialogOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" form="client-form" ?loading=${this.saving}>Save</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'clients-page': ClientsPage;
  }
}
